import * as R from 'ramda';
import { ApiHelper } from 'utils/apiHelper';
import { McsErrors } from 'utils/McsErrors/McsErrors';

import { BillingTypes } from 'reactApp/modules/BillingModule';
import {
    BillingSaveIndividualData,
    BillingSaveLegalData,
    LegalForm,
} from 'reactApp/modules/BillingModule/BillingModule.types';

const getBaseUrl = (pid: string, clientPid?: string) =>
    clientPid
        ? `partners/${pid}/clients/${clientPid}/billing`
        : `projects/${pid}/billing`;

const loadData = async (pid: string, clientPid?: string) => {
    const data = await ApiHelper.rpcCall<BillingTypes.BillingInfo>({
        url: getBaseUrl(pid, clientPid),
    });

    data.balance = Number(data.balance) || 0;
    data.bonus = Number(data.bonus) || 0;

    return data;
};

const loadMoreInfo = async (pid: string, clientPid?: string) => {
    return ApiHelper.rpcCall<BillingTypes.BillingMoreInfo>({
        url: `${getBaseUrl(pid, clientPid)}/moreinfo`,
    });
};

function pay(pid, clientPid, amount: string) {
    return ApiHelper.rpcCall<BillingTypes.BillingMoreInfo>({
        url: `${getBaseUrl(pid, clientPid)}/card/pay`,
        method: 'POST',
        data: {
            amount,
            description: 'Пополнить счет MCS на ' + amount + ' руб.',
        },
    });
}
const searchInn = async (
    pid: string,
    clientPid: string,
    inn: string,
    legalForm?: LegalForm,
) => {
    const result = await ApiHelper.rpcCall<
        BillingTypes.BillingSearchInnResultApiEntity[]
    >({
        url: `${getBaseUrl(pid, clientPid)}/registration`,
        data: {
            legal_form: legalForm,
            inn,
        },
    });

    return deserializeSearchInnResult(result);
};

function deserializeSearchInnResult(
    result: BillingTypes.BillingSearchInnResultApiEntity[],
): BillingTypes.BillingSearchInnResult[] {
    return result.map((data) => {
        const ul = data.UL;
        const ip = data.IP;
        const result = {
            inn: data.inn,
            ogrn: data.ogrn,
        } as any;
        if (ul) {
            result.legalForm = LegalForm.legal;
            result.kpp = ul.kpp;
            result.legalName = ul.legalName.full;

            const legalAddress = ul.legalAddress?.parsedAddressRF;

            if (legalAddress) {
                result.zipCode = legalAddress.zipCode;
                result.regionCode = legalAddress.regionCode;

                result.legalCity =
                    legalAddress.city?.topoValue ||
                    legalAddress.settlement?.topoValue ||
                    legalAddress.regionName?.topoValue;
                result.legalStreet = legalAddress.street?.topoValue;

                result.legalHouse = legalAddress.bulk
                    ? `${legalAddress.house?.topoValue}, ${legalAddress.bulk.topoShortName} ${legalAddress.bulk.topoValue}`
                    : legalAddress.house?.topoValue;
            }
        } else {
            result.legalForm = LegalForm.individual;
            result.legalName = ip?.fio;
        }

        return result;
    });
}

async function saveLegalInfo(
    pid: string,
    clientPid: string,
    legalInfo: BillingSaveLegalData,
) {
    try {
        return await ApiHelper.rpcCall({
            url: `${getBaseUrl(pid, clientPid)}/registration/legal`,
            method: 'POST',
            data: {
                registration_info: legalInfo,
            },
        });
    } catch (error) {
        console.error(error);
        throw McsErrors.getFormMessagesFromResponse(error, {
            fieldTexts: {
                default: 'Введено некорректное значение',
                phone: {
                    argument_invalid: 'Некорректный номер телефона',
                },
                legal_post: {
                    argument_invalid: 'Неверный почтовый индекс',
                    argument_required: 'Заполните почтовый индекс',
                },
                legal_city: {
                    argument_invalid: 'Неверный город',
                    argument_required: 'Заполните город',
                },
                legal_street: {
                    argument_invalid: 'Неверная улица',
                },
                legal_house: {
                    argument_invalid:
                        'Неверный номер дома (пример: 39\u00A0стр\u00A079)',
                },
            },
            commonTexts: {
                default: 'При сохранении данных произошла ошибка',
            },
        });
    }
}

async function saveIndividualInfo(
    pid: string,
    clientPid: string,
    individualInfo: BillingSaveIndividualData,
) {
    try {
        return await ApiHelper.rpcCall({
            url: `${getBaseUrl(pid, clientPid)}/registration/individual`,
            method: 'POST',
            data: {
                registration_info: individualInfo,
            },
        });
    } catch (error) {
        console.error(error);
        throw McsErrors.getFormMessagesFromResponse(error, {
            fieldTexts: {
                default: 'Введено некорректное значение',
                phone: {
                    argument_invalid: 'Некорректный номер телефона',
                },
                physical_address: {
                    argument_invalid: 'Неверный адрес',
                },
            },
            commonTexts: {
                default: 'При сохранении данных произошла ошибка',
            },
        });
    }
}

const BillingApi = {
    saveLegalInfo,
    saveIndividualInfo,
    loadMoreInfo,
    loadData,
    pay,
    searchInn,
};

export const getBillingApi = (pid: string, clientPid?: string) => {
    return (R.mapObjIndexed(
        // @ts-ignore
        (method) => (...args) => method(pid, clientPid, ...args),
        BillingApi,
    ) as any) as {
        loadData(): Promise<BillingTypes.BillingInfo>;
        loadMoreInfo(): Promise<BillingTypes.BillingInfo>;
        pay(amount: string): Promise<BillingTypes.BillingPayResponse>;
        searchInn(
            inn: string,
            legalForm?: LegalForm,
        ): Promise<BillingTypes.BillingSearchInnResult[]>;
        saveLegalInfo(
            legalInfo: BillingSaveLegalData,
        ): Promise<BillingTypes.BillingInfo>;
        saveIndividualInfo(
            individualInfo: BillingSaveIndividualData,
        ): Promise<BillingTypes.BillingInfo>;
    };
};
